import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Trophy } from "lucide-react";

export default function QuizModal({ open, quiz, onClose, onComplete }) {
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && quiz) {
      setAnswers(Array(quiz.questions.length).fill(-1));
      setSubmitted(false);
      setResult(null);
    }
  }, [open, quiz]);

  if (!quiz) return null;

  const submit = async () => {
    if (answers.some((a) => a < 0)) {
      toast.error("Answer all questions before submitting");
      return;
    }
    try {
      const { data } = await api.post("/quizzes/attempt", {
        quiz_id: quiz.id,
        answers,
      });
      setResult(data);
      setSubmitted(true);
      if (data.passed) {
        toast.success(`Passed with ${data.score}%`);
      } else {
        toast.error(`Scored ${data.score}% — try again`);
      }
      onComplete?.();
    } catch (err) {
      toast.error("Quiz submission failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-none border-2 border-foreground p-0 hard-shadow" data-testid="quiz-modal">
        <DialogHeader className="border-b border-border bg-foreground p-5 text-background">
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-background">
            {quiz.title}
          </DialogTitle>
          <div className="mono text-[10px] uppercase tracking-widest text-background/60">
            {quiz.questions.length} questions · pass mark {quiz.passing_score}%
          </div>
        </DialogHeader>

        <div className="space-y-6 p-6">
          {quiz.questions.map((q, qi) => (
            <div key={q.id} className="border border-border p-4">
              <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                Question {qi + 1}
              </div>
              <div className="font-semibold">{q.question}</div>
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[qi] === oi;
                  const correct = submitted && oi === q.correct_index;
                  const wrongPick = submitted && selected && oi !== q.correct_index;
                  return (
                    <button
                      key={oi}
                      type="button"
                      disabled={submitted}
                      onClick={() => {
                        const next = [...answers];
                        next[qi] = oi;
                        setAnswers(next);
                      }}
                      className={`flex w-full items-start gap-2 border p-2.5 text-left text-sm transition-colors ${
                        correct
                          ? "border-accent bg-accent/10"
                          : wrongPick
                          ? "border-destructive bg-destructive/10"
                          : selected
                          ? "border-foreground bg-muted"
                          : "border-border hover:bg-muted/50"
                      }`}
                      data-testid={`quiz-q${qi}-opt${oi}`}
                    >
                      <span className="mono mt-0.5 text-[10px] uppercase">{String.fromCharCode(65 + oi)}.</span>
                      <span className="flex-1">{opt}</span>
                      {correct && <CheckCircle2 className="h-4 w-4 text-accent" />}
                      {wrongPick && <XCircle className="h-4 w-4 text-destructive" />}
                    </button>
                  );
                })}
              </div>
              {submitted && q.explanation && (
                <div className="mono mt-3 border-l-2 border-foreground bg-muted p-2.5 text-xs">
                  {q.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 border-t border-border bg-background p-4">
          {submitted && result ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy
                  className={`h-6 w-6 ${result.passed ? "text-accent" : "text-muted-foreground"}`}
                />
                <div>
                  <div className="font-display text-2xl font-extrabold">
                    {result.score}%
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {result.correct} / {result.total} correct ·{" "}
                    {result.passed ? "PASSED" : "FAILED"}
                  </div>
                </div>
              </div>
              <Button
                onClick={onClose}
                className="rounded-none bg-foreground text-background hard-shadow-sm"
                data-testid="quiz-close-btn"
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="rounded-none"
                data-testid="quiz-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                className="rounded-none bg-foreground text-background hard-shadow-sm"
                data-testid="quiz-submit-btn"
              >
                Submit Quiz →
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
